import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UserCircle, FileText, Download, Upload, CalendarClock, Coffee, ClipboardList, Plus, Play, Square, AlertTriangle } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const statusBadge = {
  Pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800",
  Approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
  Rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800",
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDur = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
const LIMIT = 15 * 60;

export default function EmployeeSpace() {
  const { user, refreshUser } = useAuth();
  const [docs, setDocs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [, setTick] = useState(0);
  const [kind, setKind] = useState("Leave");
  const [form, setForm] = useState({ start_date: todayIso(), end_date: todayIso(), day: todayIso(), from_time: "13:00", to_time: "13:15", reason: "" });
  const [busy, setBusy] = useState(false);
  const photoInput = useRef(null);

  const loadDocs = () => api.get("/me/documents").then((r) => setDocs(r.data)).catch(() => {});
  const loadReqs = () => api.get("/me/requests").then((r) => setReqs(r.data)).catch(() => {});
  const loadBreaks = () => api.get("/me/breaks").then((r) => setBreaks(r.data)).catch(() => {});
  useEffect(() => { loadDocs(); loadReqs(); loadBreaks(); }, []);

  const activeBreak = breaks.find((b) => !b.ended_at);
  useEffect(() => {
    if (!activeBreak) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [activeBreak]);

  const elapsed = (b) => (b.ended_at ? b.duration_seconds : (Date.now() - new Date(b.started_at).getTime()) / 1000);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Photo updated");
    } catch (err) { toast.error("Could not upload photo"); }
  };

  const startBreak = async () => {
    try { await api.post("/me/breaks/start"); loadBreaks(); toast.success("Break started"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not start break"); }
  };
  const stopBreak = async (id) => {
    try {
      const { data } = await api.post(`/me/breaks/${id}/stop`);
      loadBreaks();
      toast[data.exceeded ? "warning" : "success"](data.exceeded ? "Break ended — over 15 min, HR notified" : "Break ended");
    } catch (err) { toast.error("Could not end break"); }
  };

  const submitRequest = async () => {
    if (!form.reason.trim()) { toast.error("Please add a reason"); return; }
    setBusy(true);
    const payload = { kind, reason: form.reason };
    if (kind === "Leave") { payload.start_date = form.start_date; payload.end_date = form.end_date; }
    else { payload.day = form.day; payload.from_time = form.from_time; payload.to_time = form.to_time; }
    try {
      await api.post("/me/requests", payload);
      toast.success(`${kind} request submitted`);
      setForm({ ...form, reason: "" });
      loadReqs();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit request"); }
    finally { setBusy(false); }
  };

  const BreakSlot = ({ n }) => {
    const b = breaks.find((x) => x.break_no === n);
    const nextToStart = !b && breaks.length + 1 === n && !activeBreak;
    if (b && !b.ended_at) {
      const secs = elapsed(b);
      const over = secs > LIMIT;
      return (
        <div className={`flex items-center justify-between rounded-sm border p-3 ${over ? "border-red-400 bg-red-50 dark:bg-red-950/20" : "border-primary/40 bg-primary/5"}`} data-testid={`break-slot-${n}`}>
          <div><p className="text-sm font-medium">Break {n} · running</p><p className={`font-mono text-lg tabular-nums ${over ? "text-red-600" : "text-primary"}`} data-testid={`break-timer-${n}`}>{fmtDur(secs)}</p></div>
          <Button size="sm" variant="destructive" className="rounded-sm gap-1.5" onClick={() => stopBreak(b.id)} data-testid={`stop-break-${n}`}><Square size={14} /> Stop</Button>
        </div>
      );
    }
    if (b && b.ended_at) {
      return (
        <div className="flex items-center justify-between rounded-sm border border-border p-3" data-testid={`break-slot-${n}`}>
          <div><p className="text-sm font-medium">Break {n} · done</p><p className="font-mono text-sm text-muted-foreground">{fmtDur(b.duration_seconds)}</p></div>
          {b.exceeded ? <Badge variant="outline" className="rounded-sm bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 gap-1"><AlertTriangle size={12} /> Over 15m</Badge>
            : <Badge variant="outline" className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400">On time</Badge>}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-between rounded-sm border border-dashed border-border p-3" data-testid={`break-slot-${n}`}>
        <p className="text-sm text-muted-foreground">Break {n}</p>
        <Button size="sm" className="rounded-sm gap-1.5" disabled={!nextToStart} onClick={startBreak} data-testid={`start-break-${n}`}><Play size={14} /> Start</Button>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="col-span-12 lg:col-span-4 space-y-4">
        <div className="rounded-sm border border-border bg-card p-6 text-center shadow-sm">
          {user?.photo_path
            ? <img src={fileUrl(user.photo_path)} alt={user?.name} className="w-24 h-24 rounded-full border-2 border-border object-cover mx-auto" data-testid="my-photo" />
            : <div className="w-24 h-24 rounded-full border-2 border-border bg-primary/10 text-primary flex items-center justify-center mx-auto"><UserCircle size={48} /></div>}
          <h2 className="text-xl font-heading font-bold tracking-tight mt-4" data-testid="my-name">{user?.name}</h2>
          <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
          <p className="text-xs text-muted-foreground mt-1">{user?.email}</p>
          <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
          <Button variant="outline" className="rounded-sm gap-2 mt-4 w-full" onClick={() => photoInput.current?.click()} data-testid="change-photo-btn"><Upload size={15} /> {user?.photo_path ? "Change photo" : "Upload photo"}</Button>
        </div>

        <div className="rounded-sm border border-border bg-card p-5 space-y-3" data-testid="breaks-card">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-semibold flex items-center gap-2"><Coffee size={16} className="text-primary" /> Breaks today</h3>
            <span className="text-sm text-muted-foreground" data-testid="breaks-used">{breaks.length} / 2</span>
          </div>
          <p className="text-xs text-muted-foreground">Two breaks a day, 15 minutes each. Tap start, tap stop — HR is alerted if a break runs over.</p>
          <BreakSlot n={1} />
          <BreakSlot n={2} />
        </div>
      </div>

      <div className="col-span-12 lg:col-span-8">
        <Tabs defaultValue="requests">
          <TabsList className="rounded-sm">
            <TabsTrigger value="requests" className="rounded-sm gap-1.5" data-testid="tab-requests"><ClipboardList size={14} /> Requests</TabsTrigger>
            <TabsTrigger value="documents" className="rounded-sm gap-1.5" data-testid="tab-documents"><FileText size={14} /> Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-4 space-y-5 animate-in fade-in duration-300">
            <div className="rounded-sm border border-border bg-card p-6 space-y-4">
              <h3 className="font-heading font-semibold flex items-center gap-2"><Plus size={16} className="text-primary" /> New Request</h3>
              <div className="flex flex-wrap gap-2">
                {["Leave", "Permission"].map((k) => (
                  <Button key={k} variant={kind === k ? "default" : "outline"} className="rounded-sm" onClick={() => setKind(k)} data-testid={`kind-${k.toLowerCase()}`}>{k}</Button>
                ))}
              </div>
              {kind === "Leave" ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="grid gap-1.5"><Label>From</Label><Input type="date" className="rounded-sm" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" /></div>
                  <div className="grid gap-1.5"><Label>To</Label><Input type="date" className="rounded-sm" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" /></div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="grid gap-1.5"><Label>Date</Label><Input type="date" className="rounded-sm" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="req-day" /></div>
                  <div className="grid gap-1.5"><Label>From</Label><Input type="time" className="rounded-sm" value={form.from_time} onChange={(e) => setForm({ ...form, from_time: e.target.value })} data-testid="req-from" /></div>
                  <div className="grid gap-1.5"><Label>To</Label><Input type="time" className="rounded-sm" value={form.to_time} onChange={(e) => setForm({ ...form, to_time: e.target.value })} data-testid="req-to" /></div>
                </div>
              )}
              <div className="grid gap-1.5"><Label>Reason</Label><Textarea className="rounded-sm" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason for your request…" data-testid="req-reason" /></div>
              <Button className="rounded-sm gap-2" onClick={submitRequest} disabled={busy} data-testid="submit-request-btn"><CalendarClock size={15} /> Submit {kind} Request</Button>
            </div>

            <div className="rounded-sm border border-border bg-card overflow-hidden">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold">Request History</h3></div>
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Type</TableHead><TableHead>When</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>HR Note</TableHead></TableRow></TableHeader>
                <TableBody data-testid="my-requests-table">
                  {reqs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No requests yet.</TableCell></TableRow>}
                  {reqs.map((r) => (
                    <TableRow key={r.id} data-testid={`my-request-${r.id}`}>
                      <TableCell className="font-medium">{r.kind}</TableCell>
                      <TableCell className="text-sm font-mono">{r.kind === "Leave" ? `${r.start_date} → ${r.end_date}` : `${r.day} ${r.from_time || ""}-${r.to_time || ""}`}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{r.reason || "—"}</TableCell>
                      <TableCell><Badge variant="outline" className={`rounded-sm ${statusBadge[r.status]}`}>{r.status}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.decision_comment || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-4 animate-in fade-in duration-300">
            <div className="rounded-sm border border-border bg-card overflow-hidden">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold">My Documents</h3></div>
              <div className="divide-y divide-border" data-testid="my-documents-list">
                {docs.length === 0 && <p className="px-6 py-10 text-center text-sm text-muted-foreground">No documents yet. Your HR team will upload payslips, offer letters and other files here.</p>}
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-6 py-3 hover:bg-muted/50 transition-colors" data-testid={`my-doc-${d.id}`}>
                    <div className="h-9 w-9 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><FileText size={16} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.label || d.original_filename}</p>
                      <p className="text-xs text-muted-foreground">{d.doc_type} · {new Date(d.created_at).toLocaleDateString()}</p>
                    </div>
                    <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" download={d.original_filename} className="text-primary hover:opacity-80 flex items-center gap-1.5 text-sm" data-testid={`download-doc-${d.id}`}><Download size={15} /> Download</a>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
